import { useEffect, useState } from "react";
import { useFirstMountState } from "react-use";
import { RouteFactory } from "@app/routePaths";
import { useHookUpdatePulseUrl } from "@features/pulse/hooks/useHookUpdatePulseUrl";
import useSendPulseGaData from "@features/pulse/hooks/useSendPulseGaData";
import { sortPulseArticles } from "@features/pulse/utils/helpers";
import { SanitizedPulseData } from "@features/pulse/utils/types";
import { PulseArticleObject } from "@features/pulse/variant/others/utils/types";

/**
 * This hook sets the sorted pulse articles and individual pulse articles.
 * It sorts the articles based on the provided pulseArticles and pulseIndividualArticles,
 * and updates the URL to reflect the first article's ID.
 */
export const useSetPulseArticle = ({
  pulseArticles,
  pulseIndividualArticles,
  variant,
}: PulseArticleObject) => {
  const [sortedPulseArticles, setSortedPulseArticles] = useState<
    SanitizedPulseData[]
  >([]);
  const [pulseIndividualArticle, setPulseIndividualArticle] = useState<
    SanitizedPulseData[]
  >([]);

  const { updatePulseUrl } = useHookUpdatePulseUrl();
  const { sendTrackingData } = useSendPulseGaData();

  const isFirstMount = useFirstMountState();

  useEffect(() => {
    if (pulseArticles.length === 0) return;
    if (!isFirstMount) return;

    const sortedPulseArticles = sortPulseArticles({
      pulseArticles,
      pulseIndividualArticles,
    });

    setSortedPulseArticles(sortedPulseArticles);
    setPulseIndividualArticle(pulseIndividualArticles || []);

    updatePulseUrl(sortedPulseArticles[0].id);
  }, [isFirstMount, pulseArticles, pulseIndividualArticles, updatePulseUrl]);

  useEffect(() => {
    if (!variant) return;
    if (sortedPulseArticles.length === 0) return;

    const firstArticle = sortedPulseArticles?.[0];

    const { title, id: activeId } = firstArticle;
    const virtualUrl = RouteFactory.pulseSlide(activeId);

    sendTrackingData(
      title,
      virtualUrl,
      activeId,
      sortedPulseArticles,
      "1",
      variant
    );
  }, [sortedPulseArticles, variant, sendTrackingData]);

  return { sortedPulseArticles, pulseIndividualArticle };
};
